import React from "react";

// Liz's closest friend is blind. This is not a fallback: it is the first thing
// in the document, so a screen-reader or keyboard user meets it before
// anything else, even though CSS renders the link at the bottom of the sheet.
export const DESCRIPTION_ID = "plate-description";

export const READABLE_HREF = "/readable";
export const READABLE_LABEL = "Read this drawing as text";

/**
 * The link to the readable site and the described visual. Render this FIRST in
 * the page, before the sheet: DOM order is focus order, and the skip link must
 * be the first focusable element. Its visual position (bottom of the sheet) is
 * set by CSS only.
 *
 * `plate` must be true only while the drawing is actually mounted: the
 * description is false on any page that has no drawing, so it is not rendered
 * (not merely hidden) otherwise.
 *
 * The copy below is the wording Liz reviewed (spec section 7). Do not edit it
 * here without editing the spec.
 */
export default function AccessibleEntry({ plate = true }) {
  return (
    <>
      {/* A link outside any landmark is invisible to a screen reader's landmark
          list. The nav wrapper has no box of its own: the link stays fixed. */}
      <nav aria-label="Readable version">
        <a className="plate-skip" href={READABLE_HREF}>
          {READABLE_LABEL}
        </a>
      </nav>
      {plate ? (
      <section id={DESCRIPTION_ID} className="plate-described-visual sr-only" aria-label="What this page shows">
        <p>
          <strong>What this page shows.</strong> A technical drawing of the systems I build, drawn
          the way an engineering plate is drawn: a ruled border with zone letters and
          numbers down the sides, a legend at lower left, an index of capability at
          lower centre, and a title block at lower right carrying my name, the sheet
          number S-1, and the revision date.
        </p>
        <p>
          <strong>The drawing itself</strong> is a model you can rotate. About fifteen shapes float
          in a shallow depth, joined by thin lines. Shape carries meaning: a circle is
          a system, a circle with a filled centre is a method, a square with a
          crosshair is silicon, a triangle is flight software, a hexagon is a course,
          a diamond is a publication. A dashed outline means the source is closed.
          Line style carries meaning too: a solid line is an interface between two
          things, a short dash means one produced the other, a long dash means one was
          thrown off by work on the other, and a fine red dotted line marks a boundary
          I am not sure I have drawn in the right place.
        </p>
        <p>
          <strong>What it does.</strong> Dragging rotates the model; the shapes keep facing you, so
          they stay readable at any angle. Shapes that contain other things show a plus
          sign and a count {"—"} clicking opens them and the contents appear around the
          parent. Shapes that link somewhere show a small arrow. Clicking any shape
          opens a panel listing what it is, when its code last changed, whether it
          reads as active or degrading, and every link it carries. You can also drag a shape
          itself to move it and adjust its cluster. Nothing moves on its own.
        </p>
        <p>
          <strong>Everything here is also on the readable version</strong>, including the links and
          the activity signals.
        </p>
      </section>
      ) : null}
    </>
  );
}
